import { AxisCode } from '@atlas/types'
import { Button, Combobox, type ComboboxOption } from '@atlas/ui-primitives'
import { useState } from 'react'
import { ChipSet } from '@/components/classification/ChipSet'

const AXIS_LABELS = { [AxisCode.ORDER_CODE]: 'Order' }

export interface OrderLinkFieldProps {
	/** Order codes this message is linked to. */
	value: string[]
	/** Selectable orders — `value` is the order CODE, which is what gets stored. */
	options: ComboboxOption[]
	onChange: (next: string[]) => void
}

/**
 * The work orders a message belongs to, edited as chips over the `order_code`
 * attribute.
 *
 * `order_code` is what every order-scoped surface resolves copy through — the
 * Messages section of the work order, the validation-asset gate, the PDF render —
 * so it is the link, and the message's dimension tuple is not. Classifying a
 * message like an order says what the copy is about; only this puts it ON the order.
 *
 * Multi-valued because the attribute is: the same copy legitimately serves several
 * orders, and a single-value field would silently unlink the others on every save.
 * Presentational + storied.
 */
export function OrderLinkField({ value, options, onChange }: OrderLinkFieldProps) {
	const [pick, setPick] = useState<string | undefined>(undefined)

	function add() {
		if (!pick) return
		if (!value.includes(pick)) onChange([...value, pick])
		setPick(undefined)
	}

	return (
		<div>
			{value.length === 0 ? (
				<p className="mb-2 font-mono text-xs text-ds-text-subtle">
					Not on any work order — link one to make this copy show in its Messages section.
				</p>
			) : (
				<div className="mb-2">
					<ChipSet
						tuple={{ [AxisCode.ORDER_CODE]: value }}
						axisLabels={AXIS_LABELS}
						onRemove={(_axis, code) => onChange(value.filter((c) => c !== code))}
					/>
				</div>
			)}
			<div className="flex flex-wrap items-end gap-2">
				<div className="w-72">
					<Combobox
						aria-label="Work order"
						options={options}
						value={pick ?? null}
						onChange={setPick}
						placeholder="Add work order…"
					/>
				</div>
				<Button
					type="button"
					variant="secondary"
					size="sm"
					className="rounded-ds-full"
					isDisabled={!pick}
					onPress={add}
				>
					＋ link order
				</Button>
			</div>
		</div>
	)
}
